import { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import AddCustomizedApplications from "./AddCustomizedApplications";
import WebService from "../../utility/WebService";
import { Tabs, Tab } from "react-bootstrap";
import { PencilFill, TrashFill } from "react-bootstrap-icons";
import AddEventsGallery from "./AddEventGallery";
import { toast } from "react-toastify";
import HelperService from "../../utility/HelperService";

const CustomizedApplication = () => {
  const [isLoading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [data, setData] = useState<any>(null);

  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [eventMode, setEventMode] = useState<"add" | "edit">("add");
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    setLoading(true);
    WebService.getAPI({
      action: `customized-applications/getContent`,
    })
      .then((res: any) => {
        const perm = res?.data?.permissions || {};
        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });
        setData(res.data.data);
        setLoading(false);
      })
      .catch((err: any) => {
        setLoading(false);
      });
  };

  const reorderEvents = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    const updatedEvents = [...data.events];
    const [moved] = updatedEvents.splice(fromIndex, 1);
    updatedEvents.splice(toIndex, 0, moved);

    const body = new FormData();
    body.append("events", JSON.stringify(updatedEvents));
    body.append("id", data._id);

    WebService.fileUploadAPI({
      action: `customized-applications/addEvents`,
      body,
    })
      .then(() => getData())
      .catch(() => toast.error("Failed to reorder events"));
  };

  const Metric = ({ label, value }: { label: string; value: any }) => (
    <div
      style={{
        background: "#f4fbff",
        padding: 14,
        borderRadius: 12,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 12, color: "#777" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600 }}>{value}</div>
    </div>
  );

  return (
    <>
      <AddCustomizedApplications
        show={showAddModal}
        handleClose={() => {
          setShowAddModal(false);
          getData();
        }}
        data={data}
      />
      <AddEventsGallery
        show={showEventModal}
        handleClose={() => {
          setShowEventModal(false);
          getData();
        }}
        data={data}
        event={editingEvent}
        mode={eventMode}
      />
      <div className="events-main">
        <Card className="event-card">
          <div className="d-flex justify-content-between align-items-center">
            <div className="page-title">
              Customized Applications for Rural Economies
            </div>
          </div>
          <Tabs defaultActiveKey="info" className="mt-3">
            {/* ================= INFO TAB ================= */}
            {/* ================= INFO TAB ================= */}
            <Tab eventKey="info" title="Info">
              <div className="p-3">
                {isLoading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: 120 }}
                  >
                    <span className="spinner-border" />
                  </div>
                ) : (
                  <>
                    {/* TOP ACTION */}
                    <div className="d-flex justify-content-end mb-3">
                      {pagePermission.canAdd && (
                        <CustomButton
                          isLoading={false}
                          label={data ? "Edit" : "Add"}
                          onClick={() => setShowAddModal(true)}
                          style={{ width: 100 }}
                        />
                      )}
                    </div>

                    {/* LOGO */}
                    {data?.logo && (
                      <div className="mb-4">
                        <img
                          src={data.logo}
                          alt="Logo"
                          style={{
                            width: 260,
                            objectFit: "contain",
                          }}
                          className="border rounded-3 p-2"
                        />
                      </div>
                    )}

                    {/* TITLE */}
                    {data?.title && (
                      <>
                        <strong>Title</strong>
                        <div className="mb-3">{data.title}</div>
                      </>
                    )}

                    {/* ABOUT */}
                    {data?.about && (
                      <>
                        <strong>About</strong>
                        <div className="ql-snow">
                          <div
                            className="mb-4 px-0 ql-editor"
                            dangerouslySetInnerHTML={{ __html: HelperService.getPreviewableHTML(data.about) }}
                          />
                        </div>
                      </>
                    )}

                    {/* IMAGE */}
                    {data?.image && (
                      <>
                        <strong>Image</strong>
                        <div className="d-flex flex-wrap gap-3 mt-2 mb-4">
                          {Array.isArray(data.image) ? (
                            data.image.map((img: string, index: number) => (
                              <div
                                key={index}
                                className="border rounded p-2"
                                style={{ width: 220 }}
                              >
                                <img
                                  src={img}
                                  style={{
                                    width: "100%",
                                    height: 140,
                                    objectFit: "cover",
                                    borderRadius: 6,
                                  }}
                                />
                              </div>
                            ))
                          ) : (
                            <div
                              className="border rounded p-2"
                              style={{ width: 220 }}
                            >
                              <img
                                src={data.image}
                                style={{
                                  width: "100%",
                                  height: 140,
                                  objectFit: "cover",
                                  borderRadius: 6,
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* MISSION CONTENT */}
                    {data?.missionContent && data.missionContent.length > 0 && (
                      <>
                        <strong>Mission Content</strong>
                        <div className="d-flex flex-wrap gap-3 mt-2 mb-4">
                          {data.missionContent.map(
                            (item: any, index: number) => (
                              <div
                                key={index}
                                className="border rounded p-3"
                                style={{ width: 300, background: "#fff" }}
                              >
                                {item.image && (
                                  <img
                                    src={item.image}
                                    style={{
                                      width: "100%",
                                      height: 140,
                                      objectFit: "cover",
                                      borderRadius: 6,
                                    }}
                                    className="mb-2"
                                  />
                                )}
                                <strong>{item.title}</strong>
                                <div
                                  className="text-muted mt-1"
                                  dangerouslySetInnerHTML={{
                                    __html: HelperService.getPreviewableHTML(item.description),
                                  }}
                                />
                              </div>
                            ),
                          )}
                        </div>
                      </>
                    )}

                    {/* PUBLICATIONS */}
                    {data?.publication && data.publication.length > 0 && (
                      <>
                        <strong>Publications & Resources</strong>
                        <div className="d-flex flex-wrap gap-3 mt-2 mb-4">
                          {data.publication.map((item: any, index: number) => (
                            <a
                              key={index}
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="border rounded p-3"
                              style={{
                                width: 260,
                                textDecoration: "none",
                                color: "#0d6efd",
                              }}
                            >
                              {item.image && (
                                <img
                                  src={item.image}
                                  style={{ height: 36, marginBottom: 8 }}
                                />
                              )}
                              <div style={{ fontWeight: 600 }}>
                                {item.title}
                              </div>
                            </a>
                          ))}
                        </div>
                      </>
                    )}

                    {/* NEWSLETTER */}
                    {data?.newsLetter && data.newsLetter.length > 0 && (
                      <>
                        <strong>Newsletter</strong>

                        <div
                          style={{
                            display: "flex",
                            gap: 24,
                            flexWrap: "wrap",
                            marginBottom: "10px",
                          }}
                        >
                          {data.newsLetter.map((item: any, index: number) => (
                            <a
                              key={index}
                              href={item.file}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                width: 320, // ✅ FIXED CARD WIDTH
                                textDecoration: "none",
                                background: "#fff",
                                borderRadius: 16,
                                border: "1px solid #e5e5e5",
                                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                                padding: 16,
                                color: "#000",
                              }}
                            >
                              {/* TOP BOX */}
                              <div
                                style={{
                                  background: "#f4fbff",
                                  borderRadius: 12,
                                  padding: "20px 12px",
                                  textAlign: "center",
                                  marginBottom: 14,
                                }}
                              >
                                <img
                                  src="https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg"
                                  alt="PDF"
                                  style={{
                                    width: 40,
                                    height: 40,
                                    marginBottom: 8,
                                  }}
                                />

                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: 14,
                                  }}
                                >
                                  {item.title}
                                </div>
                              </div>

                              {/* SUB TITLE */}
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  marginBottom: 6,
                                }}
                              >
                                {item.subTitle}
                              </div>

                              {/* DESCRIPTION */}
                              <div
                                style={{
                                  fontSize: 13,
                                  color: "#555",
                                  lineHeight: "1.4",
                                  marginBottom: 10,
                                }}
                              >
                                {item.description}
                              </div>

                              {/* DATE */}
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#777",
                                }}
                              >
                                {item.date
                                  ? new Date(item.date).toLocaleDateString(
                                    "en-GB",
                                  )
                                  : ""}
                              </div>
                            </a>
                          ))}
                        </div>
                      </>
                    )}

                    {/* PERFORMING STATES */}
                    {data?.performing_states &&
                      data.performing_states.length > 0 && (
                        <>
                          <strong>Performing States</strong>
                          <div className="d-flex flex-column gap-3 mt-3">
                            {data.performing_states.map(
                              (state: any, index: number) => (
                                <div
                                  key={index}
                                  className="border rounded p-3"
                                  style={{ background: "#fff" }}
                                >
                                  <div className="d-flex gap-3">
                                    {state.image && (
                                      <img
                                        src={state.image}
                                        style={{
                                          width: 160,
                                          height: 120,
                                          objectFit: "contain",
                                          borderRadius: 8,
                                        }}
                                      />
                                    )}
                                    <div>
                                      <h5>{state.name}</h5>
                                      <div className="text-muted">
                                        {state.overview}
                                      </div>
                                    </div>
                                  </div>

                                  {(state.women_trained ||
                                    state.income_increase ||
                                    state.machines_deployed ||
                                    state.energy_generated) && (
                                      <div
                                        className="mt-3"
                                        style={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "repeat(auto-fit, minmax(160px, 1fr))",
                                          gap: 12,
                                        }}
                                      >
                                        {state.women_trained && (
                                          <Metric
                                            label="Women Trained"
                                            value={state.women_trained}
                                          />
                                        )}
                                        {state.income_increase && (
                                          <Metric
                                            label="Income Increase"
                                            value={`${state.income_increase}%`}
                                          />
                                        )}
                                        {state.machines_deployed && (
                                          <Metric
                                            label="Machines Deployed"
                                            value={state.machines_deployed}
                                          />
                                        )}
                                        {state.energy_generated && (
                                          <Metric
                                            label="Energy Generated"
                                            value={`${state.energy_generated} kWh`}
                                          />
                                        )}
                                      </div>
                                    )}

                                  {state.partners &&
                                    state.partners.length > 0 && (
                                      <>
                                        <div className="mt-3 fw-semibold">
                                          Partners
                                        </div>
                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                          {state.partners.map(
                                            (partner: any, pIndex: number) => (
                                              <div
                                                key={pIndex}
                                                className="d-flex align-items-center gap-2 border rounded px-2 py-1"
                                              >
                                                {partner.image && (
                                                  <img
                                                    src={partner.image}
                                                    style={{
                                                      width: 32,
                                                      height: 32,
                                                    }}
                                                  />
                                                )}
                                                <span>{partner.name}</span>
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      </>
                                    )}
                                </div>
                              ),
                            )}
                          </div>
                        </>
                      )}
                  </>
                )}
              </div>
            </Tab>

            {/* ================= EVENT GALLERY TAB ================= */}
            <Tab eventKey="events" title="Event Gallery">
              <div className="p-3">
                {/* ADD BUTTON */}
                <div className="d-flex justify-content-end mb-3">
                  {pagePermission.canEdit && (
                    <CustomButton
                      label="Add Event Gallery"
                      isLoading={false}
                      onClick={() => {
                        setEventMode("add");
                        setEditingEvent(null);
                        setShowEventModal(true);
                      }}
                      style={{ width: 200 }}
                    />
                  )}
                </div>

                {/* NO EVENTS */}
                {(!data?.events || data.events.length === 0) && (
                  <div>No event gallery found</div>
                )}

                {/* EVENTS LIST */}
                {data?.events && data.events.length > 0 && (
                  <div className="d-flex flex-wrap gap-3">
                    {data.events.map((event: any, index: number) => (
                      <div
                        key={index}
                        draggable
                        onDragStart={(e) =>
                          e.dataTransfer.setData("eventIndex", index.toString())
                        }
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          const fromIndex = Number(
                            e.dataTransfer.getData("eventIndex"),
                          );
                          reorderEvents(fromIndex, index);
                        }}
                        className="border rounded p-3 bg-light"
                        style={{
                          width: 320,
                          cursor: "move",
                          background: "#fff",
                        }}
                      >
                        {/* DRAG HANDLE */}
                        <div
                          style={{
                            cursor: "grab",
                            color: "#888",
                            marginBottom: 6,
                          }}
                          title="Drag to reorder"
                        >
                          ☰
                        </div>

                        {/* IMAGE */}
                        {event.images &&
                          event.images.length > 0 &&
                          event.images[0]?.image && (
                            <img
                              src={event.images[0].image}
                              alt={event.title}
                              style={{
                                width: "100%",
                                height: 140,
                                objectFit: "contain",
                                borderRadius: 6,
                              }}
                              className="mb-2"
                            />
                          )}

                        <strong>{event.title}</strong>
                        <div
                          className="text-muted mt-1"
                          style={{ fontSize: 13 }}
                        >
                          {event.description}
                        </div>

                        {/* ACTIONS */}
                        <div className="d-flex gap-2 mt-2">
                          <PencilFill
                            className="cursor-pointer"
                            onClick={() => {
                              setEventMode("edit");
                              setEditingEvent({ ...event, index });
                              setShowEventModal(true);
                            }}
                          />
                          <TrashFill
                            color="#DC143C"
                            className="cursor-pointer"
                            onClick={() => {
                              setDeleteIndex(index);
                              setShowDeleteModal(true);
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Tab>
          </Tabs>
        </Card>

        {showDeleteModal && (
          <div
            className="modal fade show d-block"
            style={{ background: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowDeleteModal(false)}
                  />
                </div>

                <div className="modal-body">
                  Are you sure you want to delete this event gallery?
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    No
                  </button>

                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      if (deleteIndex === null) return;

                      const updated = data.events.filter(
                        (_: any, i: number) => i !== deleteIndex,
                      );

                      const body = new FormData();
                      body.append("events", JSON.stringify(updated));
                      body.append("id", data._id);

                      WebService.fileUploadAPI({
                        action: `customized-applications/addEvents`,
                        body,
                      }).then(() => {
                        toast.success("Customized application deleted");
                        setShowDeleteModal(false);
                        setDeleteIndex(null);
                        getData();
                      });
                    }}
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CustomizedApplication;
